<script setup lang="ts">
import { OdsCarousel, OdsCarouselSlide, OdsSkeleton } from "@ods/lidl-web-vuejs";
import { useI18n } from "vue-i18n";

import type { ShopTheLookItem } from "@reco/shared/src/types/recommendation";

import RecoHeading from "@reco/shared/src/components/RecoHeading.vue";
import { ColorScheme } from "@reco/shared/src/constants";
import { computed, useTemplateRef } from "vue";
import { MIN_STL_ITEMS, useLoadSTLFragments } from "../../composables/useLoadSTLFragments";

export type STLSliderProps = {
  heading?: string;
  subHeading?: string;
  headingElement?: string;
  showTitle?: boolean;
  colorScheme: ColorScheme;
  shopTheLookItems?: ShopTheLookItem[];
  creativeSlot?: string;
};
const props = defineProps<STLSliderProps>();
const { t } = useI18n();
const carouselRef = useTemplateRef<HTMLElement>("carouselRef");
const { visibleItems } = useLoadSTLFragments(props.shopTheLookItems, carouselRef);
const finalHeading = computed(() => props.heading ?? t("reco-frontend-next.Slider.shopthelook.heading"));
const finalSubHeading = computed(() => props.subHeading ?? t("reco-frontend-next.Slider.shopthelook.subheading"));

const slides = computed(() =>
  visibleItems.value.map((item, idx) => ({
    ...item,
    creativeSlot: props.creativeSlot ? `slot${props.creativeSlot}.${idx + 1}` : undefined,
  })),
);

const enoughSTLItems = computed(() => {
  return props.shopTheLookItems !== undefined && props.shopTheLookItems.length >= MIN_STL_ITEMS;
});
const enoughSTLFragments = computed(() => {
  return visibleItems.value.length >= MIN_STL_ITEMS;
});
const isSTLSliderFeatureToggleEnabled = computed(() => {
  return import.meta.env.ODJ_DEP_RECO_FRONTEND_VARS_PUBLIC_SHOW_STL == "true";
});

const shouldRenderWidget = computed(() => {
  return enoughSTLItems.value && enoughSTLFragments.value && isSTLSliderFeatureToggleEnabled.value;
});

const isSTLSliderVisible = computed(() => {
  return import.meta.env.ODJ_DEP_RECO_FRONTEND_VARS_PUBLIC_SHOW_STL;
});
</script>

<template>
  <div v-if="shouldRenderWidget && isSTLSliderVisible" class="stl-slider">
    <RecoHeading
      v-if="finalHeading && props.showTitle !== false"
      :sub-heading="finalSubHeading"
      :heading-element="props.headingElement"
      :color-scheme="props.colorScheme"
    >
      {{ finalHeading }}
    </RecoHeading>
    <div ref="carouselRef" class="stl-slider__carousel-wrap">
      <nav>
        <OdsCarousel
          :show-scroll-indicator="false"
          :gutter="0"
          :full-width="true"
          :title="finalHeading"
          :previous-slide-left="t('reco-frontend-next.Slider.scroll_left')"
          :next-slide-right="t('reco-frontend-next.Slider.scroll_right')"
          layout="flexbox"
          aria-live="none"
          item-size="auto"
        >
          <OdsCarouselSlide
            v-for="item in slides"
            :key="item.itemId"
            :aria-label="undefined"
            :data-stl-id="item.itemId"
            :data-creative-slot="item.creativeSlot"
            class="stl-slider__fragment"
          >
            <OdsSkeleton class="stl-slide-skeleton" />
          </OdsCarouselSlide>
        </OdsCarousel>
      </nav>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stl-slider {
  &__carousel-wrap {
    position: relative;

    :deep(.ods-carousel__track) {
      column-gap: var(--ods-gap-layout-l, 1rem);
    }

    :deep(.stl-slider__fragment) {
      --grid-gap: 1rem;
      --grid-padding: 1rem;
      --max-grid-width: 82.5rem;
      --grid-width: min(100vw, calc(var(--max-grid-width) + var(--grid-padding) * 2));
      --grid-tile-width: round(
        nearest,
        calc(var(--grid-width) / 12 - var(--grid-gap) * 11 / 12 - var(--grid-padding) * 2 / 12),
        1px
      );

      aspect-ratio: 1 / 1.34;
      flex-shrink: 0;
      flex-basis: 1;
      position: relative;
      width: calc(var(--grid-tile-width) * 12 + var(--grid-gap) * 11);

      @media (min-width: 90rem) {
        width: 28%;
      }
      @media (min-width: 64rem) {
        max-width: 393px;
        width: 27%;
      }
      @media (min-width: 48rem) {
        max-width: 373px;
        width: 36%;
      }
      @media (min-width: 35.5rem) {
        max-width: 327px;
        width: 42%;
      }

      .stl-slide-skeleton {
        width: 100%;
        height: 100%;
        aspect-ratio: 1 / 1.34;
        border-radius: var(--ods-radius-surface);
      }
    }
  }
}
</style>
